import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch, ApiError } from "../api/client";

/**
 * Состояние комнаты звонка для доски:
 * loading — спрашиваем сервер; ready — ссылка на комнату есть;
 * failed — несколько попыток не удались (пробуем дальше в фоне);
 * unavailable — звонок закрыт тарифом, спрашивать бесполезно.
 */
export type BoardCallSessionStatus = "loading" | "ready" | "failed" | "unavailable";

/** Паузы между повторами: быстро в начале, дальше — раз в полминуты. */
export const CALL_SESSION_RETRY_MS = [2_000, 4_000, 8_000, 15_000, 30_000] as const;

/** После скольких неудач показываем человеку «не получилось» с кнопкой. */
const FAILED_AFTER = 3;

/**
 * Ссылка на комнату звонка доски — с повторами.
 *
 * Раньше запрос шёл один раз: сеть моргнула, сервер звонилки ответил
 * медленно — и панель «Звонок» молча открывала главную страницу звонилки.
 * Преподаватель создавал там свою комнату, ученик — свою, и встретиться они
 * могли только по коду. Теперь без ссылки на комнату панель показывает
 * «Подключаем звонок…» и сама пробует ещё раз.
 *
 * `url` = null — спрашивать нечего (например, доска ещё не открыта).
 */
export function useBoardCallSession(url: string | null): {
  embedUrl: string | null;
  status: BoardCallSessionStatus;
  retry: () => void;
} {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<BoardCallSessionStatus>("loading");
  const [attemptKey, setAttemptKey] = useState(0);
  const failuresRef = useRef(0);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let timer = 0;

    const attempt = async () => {
      try {
        const data = await apiFetch<{ callEmbedUrl?: string }>(url);
        if (cancelled) return;
        const embed = typeof data.callEmbedUrl === "string" ? data.callEmbedUrl.trim() : "";
        if (!embed) throw new Error("пустая ссылка на комнату");
        failuresRef.current = 0;
        setEmbedUrl(embed);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        // Закрыто тарифом — повторять незачем.
        if (e instanceof ApiError && e.status === 403) {
          setStatus("unavailable");
          return;
        }
        failuresRef.current += 1;
        const n = failuresRef.current;
        if (n >= FAILED_AFTER) setStatus((s) => (s === "ready" ? s : "failed"));
        const wait =
          CALL_SESSION_RETRY_MS[Math.min(n - 1, CALL_SESSION_RETRY_MS.length - 1)]!;
        timer = window.setTimeout(() => void attempt(), wait);
      }
    };

    void attempt();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [url, attemptKey]);

  const retry = useCallback(() => {
    failuresRef.current = 0;
    setStatus((s) => (s === "ready" ? s : "loading"));
    setAttemptKey((k) => k + 1);
  }, []);

  return { embedUrl, status, retry };
}
